import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { BsCameraVideo } from '@react-icons/all-files/bs/BsCameraVideo';

import { EditorPlaceholder } from '../../components/EditorPlaceholder';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';

export interface VideoProps {
  src: string;
  poster?: string;
  loop?: boolean;
  muted?: boolean;
  autoplay?: boolean;
}

export function Video({ src, poster, loop, muted, autoplay }: VideoProps) {
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  const isPlaying = useRef(false);

  const play = useCallback(() => {
    if (!video) { return; }
    if (isPlaying.current) { return; }

    video.play();
    isPlaying.current = true;
  }, [video]);

  const pause = useCallback(() => {
    if (!video) { return; }
    if (!isPlaying.current) { return; }
    video.pause();
    isPlaying.current = false;
  }, [video]);

  const stop = useCallback(() => {
    if (!video) { return; }
    video.currentTime = 0;
    video.pause();
    isPlaying.current = false;
  }, [video]);

  function handleEnded() {
    isPlaying.current = false;
  }

  useEffect(() => {
    if (autoplay) { play(); }
  }, [src, loop]);

  useEffect(() => {
    autoplay ? play() : stop();
  }, [autoplay]);

  useIntersectionObserver({
    target: video,
    onIntersect: () => autoplay && play(),
    onEscape: pause,
  });

  useEffect(() => {
    function handlePageShow() {
      if (isPlaying.current) { play(); }
    }
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  if (!src) {
    return (
      <EditorPlaceholder
        icon={<BsCameraVideo />}
        message="비디오를 등록해주세요."
      />
    );
  }

  return (
    <Player
      preload="metadata"
      ref={(el) => setVideo(el)}
      src={`${src}#t=0.5`}
      loop={loop}
      muted={muted}
      controls={!autoplay}
      onEnded={handleEnded}
      playsInline
      poster={poster}
    />
  );
}

const Player = styled.video`
  width: 100%;
  display: block;
`;
